import React, { useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Button,
  Chip,
  IconButton,
  AppBar,
  Toolbar,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Grid,
  FormControl,
  Select,
  Divider,
  Badge,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { 
  VideoCall,
  Computer,
  PhoneAndroid,
  Tv,
  Star,
  Schedule,
  Message,
  ShoppingCart,
  AccountCircle,
  Settings as SettingsIcon,
  ExitToApp,
  Person,
  Help,
  ChatBubble,
} from '@mui/icons-material';
import Footer from '../components/Footer';
import logo0 from '../assets/logo0.png';
import { Link } from 'react-router-dom';

// Header Styled Components
const StyledAppBar = styled(AppBar)({
  backgroundColor: '#000000',
  boxShadow: 'none',
  borderBottom: '1px solid rgba(255, 255, 255, 0.1)'
});

const LogoImage = styled('img')({
  height: '30px',
  filter: 'brightness(0) invert(1)', // Makes the logo white
  cursor: 'pointer'
});

const HeaderIconButton = styled(IconButton)({
  color: '#ffffff',
  marginLeft: '16px',
  '&:hover': {
    backgroundColor: 'rgba(255, 255, 255, 0.1)'
  }
});

const StyledMenu = styled(Menu)(({ theme }) => ({
  '& .MuiPaper-root': {
    borderRadius: 6,
    marginTop: theme.spacing(1),
    minWidth: 180,
    boxShadow: 'rgb(255, 255, 255) 0px 0px 0px 0px, rgba(0, 0, 0, 0.05) 0px 0px 0px 1px, rgba(0, 0, 0, 0.1) 0px 10px 15px -3px, rgba(0, 0, 0, 0.05) 0px 4px 6px -2px',
  }
}));

const PageWrapper = styled(Box)({
  backgroundColor: '#f2f7ff',
  minHeight: '100vh',
  padding: '24px 0'
});

const ExpertCard = styled(Paper)({
  padding: '20px',
  marginBottom: '16px',
  border: '1px solid #e0e0e0',
  borderRadius: '8px',
  '&:hover': {
    borderColor: '#108a00',
    cursor: 'pointer'
  }
});

const ConsultButton = styled(Button)({
  backgroundColor: '#108a00',
  color: 'white',
  borderRadius: '20px',
  padding: '6px 16px',
  textTransform: 'none',
  '&:hover': {
    backgroundColor: '#0c6b00'
  }
});

const StyledBadge = styled(Badge)(({ theme }) => ({
  '& .MuiBadge-badge': {
    backgroundColor: '#108a00',
    color: 'white',
    fontWeight: 'bold',
  },
}));

const RemoteSupport = () => {
  const [selectedDevice, setSelectedDevice] = useState('');
  const [anchorEl, setAnchorEl] = useState(null);
  const [page, setPage] = useState(1);
  const expertsPerPage = 10;
  const open = Boolean(anchorEl);

  const handleProfileClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const experts = [
    {
      name: "Alex Thompson",
      specialty: "Mobile Device Expert",
      rating: 4.9,
      reviews: 156,
      availability: "Available Now",
      hourlyRate: "$45/hr",
      expertise: ["iOS", "Android", "Mobile Repairs"],
    },
    {
      name: "Sarah Chen",
      specialty: "Computer Systems Specialist",
      rating: 4.8,
      reviews: 203,
      availability: "Available in 1 hour",
      hourlyRate: "$55/hr",
      expertise: ["Windows", "MacOS", "Networking"],
    },
    {
      name: "Michael Rodriguez",
      specialty: "Apple Certified Expert",
      rating: 4.9,
      reviews: 178,
      availability: "Available Now",
      hourlyRate: "$50/hr",
      expertise: ["MacOS", "iOS", "iCloud"],
    },
    {
      name: "Emily Watson",
      specialty: "TV & Home Theater Expert",
      rating: 4.7,
      reviews: 142,
      availability: "Available in 30 min",
      hourlyRate: "$40/hr",
      expertise: ["Smart TV", "Home Theater", "Streaming Setup"],
    },
    {
      name: "David Kim",
      specialty: "Network Specialist",
      rating: 4.9,
      reviews: 167,
      availability: "Available Now",
      hourlyRate: "$60/hr",
      expertise: ["WiFi", "Router Setup", "Network Security"],
    },
    {
      name: "Lisa Anderson",
      specialty: "Windows Expert",
      rating: 4.8,
      reviews: 189,
      availability: "Available Now",
      hourlyRate: "$48/hr",
      expertise: ["Windows", "PC Repair", "Data Recovery"],
    },
    {
      name: "James Wilson",
      specialty: "Gaming Systems Expert",
      rating: 4.7,
      reviews: 134,
      availability: "Available in 2 hours",
      hourlyRate: "$45/hr",
      expertise: ["Gaming PCs", "Consoles", "Game Setup"],
    },
    {
      name: "Anna Martinez",
      specialty: "Smart Home Expert",
      rating: 4.9,
      reviews: 145,
      availability: "Available Now",
      hourlyRate: "$52/hr",
      expertise: ["Smart Devices", "Home Automation", "IoT"],
    },
    {
      name: "Robert Chang",
      specialty: "Audio/Visual Expert",
      rating: 4.8,
      reviews: 167,
      availability: "Available in 1 hour",
      hourlyRate: "$50/hr",
      expertise: ["Home Theater", "Sound Systems", "TV Mounting"],
    },
    {
      name: "Sophie Turner",
      specialty: "Mobile Repair Specialist",
      rating: 4.9,
      reviews: 198,
      availability: "Available Now",
      hourlyRate: "$47/hr",
      expertise: ["Phone Repair", "Tablet Repair", "Screen Replacement"],
    }
  ];

  const totalPages = Math.ceil(experts.length / expertsPerPage);
  const currentExperts = experts.slice(
    (page - 1) * expertsPerPage,
    page * expertsPerPage
  );

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
      {/* Black Header */}
      <StyledAppBar position="static">
        <Toolbar>
          <Container maxWidth="lg" sx={{ 
            display: 'flex', 
            justifyContent: 'space-between',
            alignItems: 'center'
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Link to="/dashboard">
                <LogoImage src={logo0} alt="Logo" />
              </Link>
              <Link 
                to="/support" 
                style={{ 
                  textDecoration: 'none', 
                  color: '#ffffff',
                  marginLeft: '24px'
                }}
              >
                <Typography 
                  variant="body1"
                  sx={{ 
                    '&:hover': { 
                      color: 'rgba(255,255,255,0.8)' 
                    } 
                  }}
                >
                  Support
                </Typography>
              </Link>
            </Box>
            <Box>
              <HeaderIconButton>
                <StyledBadge badgeContent={3}>
                  <ChatBubble />
                </StyledBadge>
              </HeaderIconButton>
              <HeaderIconButton>
                <StyledBadge badgeContent={2}>
                  <ShoppingCart />
                </StyledBadge>
              </HeaderIconButton>
              <HeaderIconButton
                onClick={handleProfileClick}
                aria-controls={open ? 'profile-menu' : undefined}
                aria-haspopup="true"
                aria-expanded={open ? 'true' : undefined}
              >
                <AccountCircle />
              </HeaderIconButton>
            </Box>
          </Container>
        </Toolbar>
      </StyledAppBar>

      {/* Profile Menu */}
      <StyledMenu
        id="profile-menu"
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        onClick={handleClose}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        <MenuItem component={Link} to="/profile">
          <ListItemIcon>
            <Person fontSize="small" />
          </ListItemIcon>
          <ListItemText>Profile</ListItemText>
        </MenuItem>
        
        <MenuItem component={Link} to="/settings">
          <ListItemIcon>
            <SettingsIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Settings</ListItemText>
        </MenuItem>

        <MenuItem component={Link} to="/help">
          <ListItemIcon>
            <Help fontSize="small" />
          </ListItemIcon>
          <ListItemText>Help Center</ListItemText>
        </MenuItem>

        <Divider />
        
        <MenuItem onClick={() => { /* Add logout logic */ }}>
          <ListItemIcon>
            <ExitToApp fontSize="small" />
          </ListItemIcon>
          <ListItemText>Logout</ListItemText>
        </MenuItem>
      </StyledMenu>
      
      <PageWrapper>
        <Container maxWidth="lg">
          {/* Hero Banner with Black Background */}
          <Paper 
            sx={{ 
              p: 4, 
              mb: 3, 
              backgroundColor: '#000000',
              color: 'white',
              borderRadius: '8px',
            }}
          >
            <Grid container spacing={3} alignItems="center">
              <Grid item xs={12} md={7}>
                <Typography variant="h4" sx={{ mb: 2 }}>
                  Expert Remote Support
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Get instant help with your devices through secure video consultations with our certified experts
                </Typography>
                <Button
                  variant="contained"
                  startIcon={<VideoCall />}
                  size="large"
                  sx={{
                    backgroundColor: '#108a00',
                    '&:hover': { backgroundColor: '#0c6b00' },
                    textTransform: 'none',
                    borderRadius: '20px',
                    px: 3
                  }}
                >
                  Start Video Diagnosis
                </Button>
              </Grid>
              <Grid item xs={12} md={5}>
                {/* Add hero image here if needed */}
              </Grid>
            </Grid>
          </Paper>

          {/* Device Selection */}
          <Paper sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" sx={{ mb: 2 }}>
              Select Your Device
            </Typography>
            <FormControl fullWidth>
              <Select
                value={selectedDevice}
                onChange={(e) => setSelectedDevice(e.target.value)}
                displayEmpty
                sx={{ mb: 2 }}
              >
                <MenuItem value="">
                  <em>Choose a device type</em>
                </MenuItem>
                <MenuItem value="phone"><PhoneAndroid sx={{ mr: 1 }} /> Smartphone</MenuItem>
                <MenuItem value="computer"><Computer sx={{ mr: 1 }} /> Computer</MenuItem>
                <MenuItem value="tv"><Tv sx={{ mr: 1 }} /> Television</MenuItem>
              </Select>
            </FormControl>
          </Paper>

          {/* Expert Listings */}
          <Box sx={{ mb: 4 }}>
            <Typography variant="h6" sx={{ mb: 2 }}>
              Available Experts
            </Typography>
          
            {currentExperts.map((expert, index) => (
              <ExpertCard elevation={0} key={index}>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={8}>
                    <Typography variant="h6" sx={{ color: '#14a800', mb: 1 }}>
                      {expert.name}
                    </Typography>
                    <Typography variant="subtitle1" sx={{ mb: 1 }}>
                      {expert.specialty}
                    </Typography>
                    
                    <Box sx={{ display: 'flex', gap: 2, mb: 2, color: '#5e6d55' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Star sx={{ fontSize: 16 }} />
                        <Typography variant="body2">{expert.rating} ({expert.reviews} reviews)</Typography>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Schedule sx={{ fontSize: 16 }} />
                        <Typography variant="body2">{expert.availability}</Typography>
                      </Box>
                      <Typography variant="body2">{expert.hourlyRate}</Typography>
                    </Box>

                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
                      {expert.expertise.map((skill, idx) => (
                        <Chip
                          key={idx}
                          label={skill}
                          size="small"
                          sx={{
                            backgroundColor: '#f2f7ff',
                            border: '1px solid #e4ebe4',
                            borderRadius: '20px',
                            color: '#5e6d55'
                          }}
                        />
                      ))}
                    </Box>
                  </Grid>
                  
                  <Grid item xs={12} md={4} sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <Button
                      variant="contained"
                      startIcon={<VideoCall />}
                      fullWidth
                      sx={{
                        backgroundColor: '#108a00',
                        '&:hover': { backgroundColor: '#0c6b00' },
                        textTransform: 'none',
                        borderRadius: '20px'
                      }}
                    >
                      Book Video Call
                    </Button>
                    <Button
                      variant="outlined"
                      startIcon={<Message />}
                      fullWidth
                      sx={{
                        borderColor: '#108a00',
                        color: '#108a00',
                        textTransform: 'none',
                        borderRadius: '20px',
                        '&:hover': {
                          borderColor: '#0c6b00',
                          backgroundColor: 'rgba(16, 138, 0, 0.04)'
                        }
                      }}
                    >
                      Start Chat
                    </Button>
                  </Grid>
                </Grid>
              </ExpertCard>
            ))}

            {/* Pagination */}
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
              <Button
                disabled={page === 1}
                onClick={() => setPage(page - 1)}
                sx={{ mr: 2 }}
              >
                Previous
              </Button>
              <Typography sx={{ mx: 2, lineHeight: '40px' }}>
                Page {page} of {totalPages}
              </Typography>
              <Button
                disabled={page === totalPages}
                onClick={() => setPage(page + 1)}
                sx={{ ml: 2 }}
              >
                Next
              </Button>
            </Box>
          </Box>
        </Container>
      </PageWrapper>

      <Footer />
    </Box>
  );
};

export default RemoteSupport; 